import { useEffect, useRef, useState } from 'react';
import dropin, { Dropin } from 'braintree-web-drop-in';
import Icon from './Icon';

interface BraintreeDropinProps {
  clientToken: string;
  amount: number;
  onPaymentSuccess: (nonce: string, paymentType: string) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
}

export default function BraintreeDropin({
  clientToken,
  amount,
  onPaymentSuccess,
  onError,
  onCancel,
}: BraintreeDropinProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropinInstance = useRef<Dropin | null>(null);
  const isInitializing = useRef(false);
  const initClientToken = useRef(clientToken); // Store initial token
  const [isReady, setIsReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Only re-initialize if clientToken actually changes (not on every render)
  useEffect(() => {
    let isMounted = true;
    
    const initDropin = async () => {
      if (!containerRef.current || !clientToken || isInitializing.current) return;
      
      // Skip if already initialized with same token
      if (dropinInstance.current && initClientToken.current === clientToken) {
        return;
      }
      
      isInitializing.current = true;
      initClientToken.current = clientToken;
      setIsReady(false);

      // Teardown existing instance first
      if (dropinInstance.current) {
        try {
          await dropinInstance.current.teardown();
        } catch {
          // Ignore teardown errors
        }
        dropinInstance.current = null;
      }

      // Clear container content
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      try {
        const instance = await dropin.create({
          authorization: clientToken,
          container: containerRef.current!,
          // PayPal disabled - requires Reference Transactions enabled in PayPal sandbox
          // To enable: PayPal Business Settings > Account Settings > Payment preferences > Reference Transactions
          // paypal: {
          //   flow: 'vault',
          //   buttonStyle: {
          //     color: 'blue',
          //     shape: 'rect',
          //     size: 'responsive',
          //   },
          // },
          card: {
            cardholderName: {
              required: true,
            },
            overrides: {
              fields: {
                number: {
                  placeholder: '4111 1111 1111 1111',
                },
                cvv: {
                  placeholder: '123',
                },
                expirationDate: {
                  placeholder: '12/26',
                },
              },
            },
          },
        });
        
        if (isMounted) {
          dropinInstance.current = instance;
          setIsReady(true);
        } else {
          instance.teardown().catch(() => {});
        }
      } catch (err) {
        console.error('Drop-in creation error:', err);
        // Don't show error for "container not empty" - it's a race condition
        if (isMounted && !(err as Error).message?.includes('empty DOM node')) {
          onError((err as Error).message || 'Failed to load payment form');
        }
      } finally {
        isInitializing.current = false;
      }
    };

    initDropin();

    return () => {
      isMounted = false;
      const instance = dropinInstance.current;
      if (instance) {
        dropinInstance.current = null;
        // Delay teardown to avoid race with blur events
        setTimeout(() => {
          instance.teardown().catch(() => {});
        }, 100);
      }
    };
  }, [clientToken]); // Only depend on clientToken, not onError

  const handleSubmit = async () => {
    if (!dropinInstance.current || isProcessing) return;

    setIsProcessing(true);
    try {
      const result = await dropinInstance.current.requestPaymentMethod();
      onPaymentSuccess(result.nonce, result.type);
    } catch (err) {
      // User cancelled or no payment method selected
      if ((err as Error).message?.includes('No payment method')) {
        onError('Please select a payment method');
      } else {
        onError((err as Error).message || 'Payment failed');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Braintree Drop-in Container */}
      <div 
        ref={containerRef} 
        className="min-h-[200px] bg-gray-50 rounded-lg p-2"
      />

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isReady || isProcessing}
          className="btn-primary flex-1 flex items-center justify-center gap-2"
        >
          {isProcessing ? (
            <>
              <span className="animate-spin">
                <Icon name="hourglass_empty" />
              </span>
              Processing...
            </>
          ) : (
            <>
              <Icon name="lock" />
              Pay £{(amount / 100).toFixed(2)}
            </>
          )}
        </button>
        
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="btn-secondary px-4"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
        <Icon name="verified_user" className="text-green-500" />
        Secured by Braintree (a PayPal service)
      </div>
    </div>
  );
}
